import { writable } from 'svelte/store';
import type { UserSocialProfileGraph } from '~/jet/models';
import type { Readable } from 'svelte/store';
import type { User } from '~/utils/user';
import type {
    MusicSubscription,
    UpsellOfferDetails,
} from '~/utils/subscription';
import type { CommerceRoute } from '~/utils/commerce/models';
import type { CommerceModalParams } from '@amp/web-app-components/src/components/Commerce/types';

interface InternalCommerceStateModifiers {
    setLoadingUser: (isLoadingUser: boolean) => void;
    setLoadingUserProfile: (isLoadingUserProfile: boolean) => void;
    setup: () => void;
    setUser: (user: User) => void;
    setUpsellOfferDetails: (upsellOfferDetails: UpsellOfferDetails) => void;
    setUserSocialProfile: (userSocialProfile: UserSocialProfileGraph) => void;
    setUserSubscriptionStatus: (subscriptionStatus: MusicSubscription) => void;
}

interface ExternalCommerceStateModifiers {
    resetUser: () => void;
    openModal: (
        activePath: CommerceRoute,
        modalParams?: { [key: string]: any },
    ) => void;
    closeModal: () => void;
}

interface ModalState {
    activePath: CommerceRoute;
    isOpen: boolean;
    modalParams: CommerceModalParams;
}

export interface CommerceState
    extends Readable<{
            gdprAcknowledged?: boolean;
            isLoadingUser?: boolean;
            isLoadingUserProfile?: boolean;
            isSetup?: boolean;
            user?: User;
            userSocialProfile?: UserSocialProfileGraph;
            upsellOfferDetails?: UpsellOfferDetails;
            modal?: ModalState;
            subscriptionStatus?: MusicSubscription;
        }>,
        ExternalCommerceStateModifiers {}

export interface CommerceStore {
    external: CommerceState;
    internal: InternalCommerceStateModifiers;
}

export const createCommerceStore = (): CommerceStore => {
    const { subscribe, update } = writable({
        isLoadingUserProfile: false,
        isLoadingUser: false,
        isSetup: false,
        user: null,
        userSocialProfile: null,
        upsellOfferDetails: null,
        subscriptionStatus: null,
    });

    const setLoadingUserProfile = (isLoadingUserProfile: boolean) =>
        update((state) => ({
            ...state,
            isLoadingUserProfile,
        }));

    const setLoadingUser = (isLoadingUser: boolean) =>
        update((state) => ({
            ...state,
            isLoadingUser,
        }));

    const setUser = (user: User) =>
        update((state) => ({
            ...state,
            user: user
                ? {
                      ...state.user,
                      ...user,
                  }
                : state.user,
        }));

    const setUpsellOfferDetails = (upsellOfferDetails: UpsellOfferDetails) =>
        update((state) => ({
            ...state,
            upsellOfferDetails,
        }));

    const setUserSocialProfile = (userSocialProfile: UserSocialProfileGraph) =>
        update((state) => ({
            ...state,
            userSocialProfile,
        }));

    const setUserSubscriptionStatus = (subscriptionStatus: MusicSubscription) =>
        update((state) => ({
            ...state,
            subscriptionStatus,
        }));

    const openModal = (
        activePath: CommerceRoute,
        modalParams: { [key: string]: any },
    ) => {
        update((state) => ({
            ...state,
            modal: {
                activePath,
                isOpen: true,
                modalParams,
            },
        }));
    };

    const closeModal = () => {
        update((state) => ({
            ...state,
            modal: {
                activePath: null,
                isOpen: false,
            },
        }));
    };

    const resetUser = () => {
        update((state) => ({
            ...state,
            user: null,
        }));
    };

    const setup = () => {
        update((state) => ({
            ...state,
            isSetup: true,
        }));
    };

    const internal = {
        setLoadingUser,
        setLoadingUserProfile,
        setup,
        setUser,
        setUpsellOfferDetails,
        setUserSocialProfile,
        setUserSubscriptionStatus,
    };
    const external = {
        openModal,
        closeModal,
        subscribe,
        resetUser,
    };

    return { internal, external };
};
